import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, ArrowLeftRight, Loader2 } from 'lucide-react';
import PhotoUpload from './PhotoUpload';

/**
 * HistoryEditor
 * -------------
 * Editor for the Women's History/Story timeline entries.
 * Storage: page_content.womens-history.history_entries = [
 *   { id, order, year, date, title, text, image, side: 'left' | 'right' }
 * ]
 * Also exposes the cover image / video and hero heading/blurb inputs.
 */

export default function HistoryEditor() {
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await api.get('/site/page-content/womens-history');
      setC(r.data || {});
    } catch { /* new page */ }
  };
  useEffect(() => { load(); }, []);
  // persist() commits the WHOLE current draft after a short debounce so typing
  // in title/text fields doesn't fire a PATCH + toast on every keystroke.
  const persistTimer = useRef(null);
  const scheduledRef = useRef(null);
  const persist = async (patch, { immediate = false, silent = false } = {}) => {
    scheduledRef.current = patch;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    const flush = async () => {
      const pending = scheduledRef.current;
      scheduledRef.current = null;
      if (!pending) return;
      setBusy(true);
      try {
        await api.patch('/site/page-content/womens-history', pending);
        await load();
        if (!silent) toast.success('Saved · live');
      } catch { toast.error('Save failed'); } finally { setBusy(false); }
    };
    if (immediate) return flush();
    persistTimer.current = setTimeout(flush, 700);
  };
  const setField = (k, v) => setC(p => ({ ...p, [k]: v }));

  const entries = (c.history_entries || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const saveHero = () => persist({
    hero_heading: c.hero_heading, hero_blurb: c.hero_blurb,
    hero_image: c.hero_image, hero_video_url: c.hero_video_url,
  });

  const addEntry = () => {
    const order = entries.length ? Math.max(...entries.map(e => e.order ?? 0)) + 1 : 0;
    const next = [...entries, {
      id: `he_${Date.now()}`, order,
      year: '2026', date: '', title: 'New Moment', text: 'Describe what happened.',
      image: '', side: entries.length % 2 === 0 ? 'left' : 'right',
    }];
    persist({ history_entries: next });
  };
  const updateEntry = (id, patch) => {
    persist({ history_entries: entries.map(e => e.id === id ? { ...e, ...patch } : e) });
  };
  const removeEntry = (id) => {
    if (!window.confirm('Remove this history entry?')) return;
    persist({ history_entries: entries.filter(e => e.id !== id) });
  };
  const move = (id, delta) => {
    const idx = entries.findIndex(e => e.id === id);
    const swap = idx + delta;
    if (swap < 0 || swap >= entries.length) return;
    const next = [...entries];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    persist({ history_entries: next.map((e, i) => ({ ...e, order: i })) });
  };
  const flipSide = (e) => updateEntry(e.id, { side: e.side === 'right' ? 'left' : 'right' });

  return (
    <div className="space-y-6">
      <div className="bg-white border rounded-lg p-6 space-y-4">
        <h4 className="font-serif text-lg text-royal font-bold">Hero / Cover</h4>
        <PhotoUpload label="Cover image" value={c.hero_image} onChange={(v) => setField('hero_image', v)} category="womens" />
        <div>
          <div className="text-sm font-semibold text-royal mb-1">Cover VIDEO (mp4 URL — overrides image if set)</div>
          <input value={c.hero_video_url || ''} onChange={(e) => setField('hero_video_url', e.target.value)}
            data-testid="womens-history-hero-video"
            placeholder="https://.../video.mp4" className="w-full px-3 py-2 border rounded font-mono text-xs" />
        </div>
        <PhotoUpload label="Or upload a cover video (mp4)" value={c.hero_video_url} onChange={(v) => setField('hero_video_url', v)} category="womens" accept="video/*" />
        <div>
          <div className="text-sm font-semibold text-royal mb-1">Hero heading</div>
          <input value={c.hero_heading || ''} onChange={(e) => setField('hero_heading', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </div>
        <div>
          <div className="text-sm font-semibold text-royal mb-1">Hero blurb</div>
          <textarea rows="2" value={c.hero_blurb || ''} onChange={(e) => setField('hero_blurb', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </div>
        <div><button onClick={saveHero} disabled={busy} data-testid="history-hero-save" className="btn-royal">{busy ? 'Saving…' : 'Save Hero'}</button></div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h4 className="font-serif text-lg text-royal font-bold">Timeline Entries ({entries.length})</h4>
          <p className="text-xs text-charcoal-muted mt-1">Each entry appears in the top timeline strip and as a paragraph with alternating photo placement. Public visitors can click any year to jump to that entry.</p>
        </div>
        <button onClick={addEntry} disabled={busy} data-testid="history-add-entry"
          className="btn-royal text-sm py-2 px-4 flex items-center gap-1">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Entry
        </button>
      </div>

      <div className="space-y-4">
        {entries.length === 0 && (
          <div className="bg-white border rounded-lg p-8 text-center text-sm text-charcoal-muted">
            No entries yet. Click <strong>Add Entry</strong> to add the first moment of the ministry's story.
          </div>
        )}
        {entries.map((e, i) => (
          <div key={e.id} data-testid={`history-entry-${e.id}`} className="bg-white border rounded-lg p-4 grid md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <PhotoUpload label={`Photo (${e.side === 'right' ? 'right' : 'left'} side)`} value={e.image} onChange={(v) => updateEntry(e.id, { image: v })} category="womens" />
              <button onClick={() => flipSide(e)} data-testid={`history-flip-${e.id}`}
                className="mt-2 text-xs px-3 py-1.5 border border-royal text-royal rounded-full hover:bg-royal hover:text-white transition flex items-center gap-1">
                <ArrowLeftRight className="w-3 h-3" /> Switch to {e.side === 'right' ? 'left' : 'right'} side
              </button>
            </div>
            <div className="md:col-span-8 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input value={e.year || ''} onChange={(ev) => updateEntry(e.id, { year: ev.target.value })}
                  data-testid={`history-year-${e.id}`}
                  placeholder="Year (e.g. 1978)" className="px-3 py-2 border rounded font-serif text-lg font-bold text-royal" />
                <input value={e.date || ''} onChange={(ev) => updateEntry(e.id, { date: ev.target.value })}
                  placeholder="Date / Season (e.g. March 12 or Spring)" className="px-3 py-2 border rounded text-sm" />
              </div>
              <input value={e.title || ''} onChange={(ev) => updateEntry(e.id, { title: ev.target.value })}
                data-testid={`history-title-${e.id}`}
                placeholder="Entry title" className="w-full px-3 py-2 border rounded font-semibold" />
              <textarea rows="5" value={e.text || ''} onChange={(ev) => updateEntry(e.id, { text: ev.target.value })}
                data-testid={`history-text-${e.id}`}
                placeholder="Paragraph text" className="w-full px-3 py-2 border rounded" />
              <div className="flex items-center gap-1 pt-1">
                <button onClick={() => move(e.id, -1)} disabled={i === 0 || busy} title="Move up" className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(e.id, 1)} disabled={i === entries.length - 1 || busy} title="Move down" className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <select value={e.side || 'left'} onChange={(ev) => updateEntry(e.id, { side: ev.target.value })} className="text-xs px-2 py-1 border rounded">
                  <option value="left">Photo left</option>
                  <option value="right">Photo right</option>
                </select>
                <button onClick={() => removeEntry(e.id)} className="ml-auto p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
